import { useEffect, useRef, useState } from 'react';
import { ApiError, errorText } from '../api';
import { eurC } from '../lib/money';
import { useToast } from '../lib/toast';
import {
  dePrice,
  DRITTLAND_HINWEISE,
  emptyRow,
  eurPrice,
  hinweisAus,
  HINWEIS_DRITTLAND,
  parsePrice,
  RATE_LABEL,
  RATE_SUM,
  summen,
  UNITS,
  vatOf,
  type PosRow,
} from './formular';
import { isBilltoro } from '../produkt';
import { LagerMarke } from './lager/LagerMarke';
import { gleicheLeistung, leistungText, positionsText, type Leistung, type Leistungen } from './LeistungDaten';
import type { Rate } from './types';
import { VorschlagInput } from './Vorschlaege';
import { inEur, preisAusEur, type Waehrung } from './waehrung';
import './formular.css';

const BILLTORO = isBilltoro();

// Positionen einer Rechnung (auch Angebot und wiederkehrende Rechnung): Leistung, Menge, Einheit, Einzelpreis netto
// (bis 4 Nachkommastellen, z. B. 0,085 € je m²), auf Wunsch Rabatt in Prozent (Spalte erst nach Klick auf „Rabatt“),
// Umsatzsteuer und Betrag. Beim Tippen schlägt das Feld „Leistung“ gespeicherte Leistungen vor (Name, Preis, Einheit,
// Steuer); eine fertige Position lässt sich „als Leistung speichern“.
//
// Props: rows/onChange (alle Zeilen, siehe formular.ts PosRow), ku (Kleinunternehmer: ohne USt-Spalte), rates (Steuerarten,
// die zur Kundenart passen), leistungen (useLeistungen(firmId) aus LeistungDaten.ts; geteilt, damit gespeicherte
// Leistungen gleich vorgeschlagen werden), ohnePreise (Lieferschein ohne Preise: nur Leistung, Menge und Einheit; Preise
// der Zeilen bleiben unsichtbar stehen), w und kurs (Währung des Belegs und Kurs in Euro je Einheit: Beträge mit dem
// Kürzel, Preise gespeicherter Leistungen (in Euro) beim Einfügen umgerechnet; in Fremdwährung kein „als Leistung
// speichern“), satz (Steuer neuer Positionen, z. B. 0 % bei einer Firma außerhalb der EU), leistungSpeichern (Billtoro:
// Recht „Kontakte, Leistungen und Lager“; ohne kein „als Leistung speichern“, der Server lehnt es ab).
// Eine aus einer Leistung übernommene Zeile merkt sich den Artikel (artikelId, unsichtbar, in beiden Produkten); in
// Billtoro steht bei einem Artikel mit „Bestand führen“ die Marke mit dem Bestand darunter (lager/LagerMarke.tsx).
export function Positionen({
  rows,
  onChange,
  ku,
  rates,
  leistungen,
  ohnePreise = false,
  w = 'EUR',
  kurs = '1',
  satz = 19,
  leistungSpeichern = true,
}: {
  rows: PosRow[];
  onChange(rows: PosRow[]): void;
  ku: boolean;
  rates: Rate[];
  leistungen: Leistungen;
  ohnePreise?: boolean;
  w?: Waehrung;
  kurs?: string | null;
  satz?: Rate;
  leistungSpeichern?: boolean;
}) {
  const toast = useToast();
  const [rabattAn, setRabattAn] = useState(false);
  const [saving, setSaving] = useState<number | null>(null);
  const rabatt = !ohnePreise && (rabattAn || rows.some((r) => r.discount.trim() !== ''));
  const preise = !ohnePreise;
  const { lines } = summen(rows, ku);
  // Zeilen beim Ende eines Speicherns (inzwischen kann weiter getippt worden sein)
  const aktuell = useRef(rows);
  aktuell.current = rows;

  const setRow = (key: number, patch: Partial<PosRow>) =>
    onChange(rows.map((r) => (r.key === key ? { ...r, ...patch, ki: false, touched: true } : r)));

  // Leistung gewählt: Text (mit Artikelnummer vorn), Einheit, Preis und Steuer übernehmen (Steuer nur, wenn sie zum
  // Kunden passt) und den Artikel merken. Fremdwährung: der Preis (in Euro) zum Kurs umgerechnet, ohne Kurs nicht
  // übernommen
  function takeLeistung(key: number, l: Leistung) {
    const r = rows.find((x) => x.key === key);
    if (!r) return;
    const fremd = w !== 'EUR' && l.price != null;
    if (fremd) toast(kurs ? 'Preis aus Euro umgerechnet.' : 'Preis nicht übernommen: Es fehlt der Kurs zum Umrechnen.');
    setRow(key, {
      description: positionsText(l),
      unit: l.unit || r.unit,
      price: l.price == null ? r.price : !fremd ? dePrice(l.price) : kurs ? dePrice(preisAusEur(l.price, kurs)) : r.price,
      rate: rates.includes(l.rate) ? l.rate : r.rate,
      tip: undefined,
      artikelId: l.id,
    });
  }

  // fertige Position als Leistung speichern (Name = Text der Position); die Zeile merkt sich dann die neue Leistung
  async function save(r: PosRow) {
    const price = parsePrice(r.price);
    setSaving(r.key);
    try {
      const l = await leistungen.add({
        name: r.description.trim().slice(0, 120),
        description: null,
        unit: r.unit.trim(),
        price: typeof price === 'number' ? price : null,
        rate: ku ? 19 : r.rate,
      });
      toast(`Leistung „${l.name}“ gespeichert`);
      onChange(aktuell.current.map((x) => (x.key === r.key ? { ...x, artikelId: l.id } : x)));
    } catch (err) {
      toast(errorText(err));
    } finally {
      setSaving(null);
    }
  }

  // Vorschläge zum getippten Text: Name enthält ihn oder die Artikelnummer beginnt damit (nicht die schon übernommene)
  const passend = (q: string) => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return leistungen.list
      .filter(
        (l) =>
          (l.name.toLowerCase().includes(t) || !!l.artikelnummer?.toLowerCase().startsWith(t)) &&
          leistungText(l).toLowerCase() !== t &&
          positionsText(l).toLowerCase() !== t,
      )
      .sort((a, b) => Number(!a.name.toLowerCase().startsWith(t)) - Number(!b.name.toLowerCase().startsWith(t)))
      .slice(0, 6);
  };

  return (
    <div className={`rt-items pos${ku ? ' ku' : ''}${rabatt ? ' mit-rabatt' : ''}${ohnePreise ? ' ohne-preise' : ''}`}>
      <div className="rt-item rt-item-head" aria-hidden>
        <span>Leistung</span>
        <span>Menge</span>
        <span>Einheit</span>
        {preise && <span>Einzelpreis netto</span>}
        {rabatt && <span>Rabatt %</span>}
        {preise && !ku && <span>USt</span>}
        {preise && <span className="r">Betrag</span>}
        <span />
      </div>
      {rows.map((r, i) => {
        const n = i + 1;
        const vor = passend(r.description);
        const p = parsePrice(r.price);
        // „als Leistung speichern“: Text und Preis da, noch keine Leistung mit diesem Namen
        const speicherbar =
          leistungSpeichern &&
          preise &&
          w === 'EUR' &&
          leistungen.loaded && r.description.trim().length > 1 && r.description.trim().length <= 120 && typeof p === 'number' && p > 0 && !gleicheLeistung(leistungen.list, r.description);
        // Billtoro: Zeile aus einem Artikel mit „Bestand führen“ (Marke darunter)
        const imLager = BILLTORO && !!r.artikelId && leistungen.list.some((l) => l.id === r.artikelId && l.lager);
        return (
          <div className={`rt-item${r.ki ? ' ki' : ''}${r.tip && !r.touched ? ' tip' : ''}`} key={r.key}>
            <label className="rt-desc">
              <span className="sr-only">Leistung {n}</span>
              <VorschlagInput
                value={r.description}
                vorschlaege={vor.map((l) => ({
                  key: l.id,
                  label: positionsText(l),
                  sub: [l.price != null ? `${eurPrice(l.price)}${l.unit ? ` je ${l.unit}` : ''}` : l.unit, ku ? '' : RATE_LABEL[String(l.rate)]]
                    .filter(Boolean)
                    .join(' · '),
                }))}
                onPick={(id) => {
                  const l = leistungen.list.find((x) => x.id === id);
                  if (l) takeLeistung(r.key, l);
                }}
                onChange={(description) => setRow(r.key, { description })}
                inputProps={{
                  'aria-label': `Leistung ${n}`,
                  placeholder: leistungen.list.length ? 'Tippen: Ihre Leistungen werden vorgeschlagen' : 'z. B. Unterhaltsreinigung Büro',
                }}
              />
            </label>
            <label>
              <span className="rt-lbl">Menge</span>
              <input aria-label={`Menge ${n}`} value={r.quantity} inputMode="decimal" onChange={(e) => setRow(r.key, { quantity: e.target.value })} />
            </label>
            <label>
              <span className="rt-lbl">Einheit</span>
              <input aria-label={`Einheit ${n}`} list="rt-units" value={r.unit} onChange={(e) => setRow(r.key, { unit: e.target.value })} />
            </label>
            {preise && (
              <label>
                <span className="rt-lbl">Preis netto</span>
                <input
                  aria-label={`Preis netto ${n}`}
                  value={r.price}
                  inputMode="decimal"
                  placeholder="0,00"
                  onChange={(e) => setRow(r.key, { price: e.target.value })}
                />
              </label>
            )}
            {rabatt && (
              <label>
                <span className="rt-lbl">Rabatt %</span>
                <input
                  aria-label={`Rabatt in Prozent ${n}`}
                  value={r.discount}
                  inputMode="decimal"
                  placeholder="0"
                  onChange={(e) => setRow(r.key, { discount: e.target.value })}
                />
              </label>
            )}
            {preise && !ku && (
              <label>
                <span className="rt-lbl">USt</span>
                <select
                  aria-label={`USt ${n}`}
                  value={String(r.rate)}
                  onChange={(e) => setRow(r.key, { rate: Number(e.target.value) as Rate })}
                >
                  {(rates.includes(r.rate) ? rates : [...rates, r.rate]).map((x) => (
                    <option key={x} value={String(x)}>
                      {RATE_LABEL[String(x)]}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {preise && <span className="rt-line num r">{lines[i] === null ? '–' : eurC(lines[i]! / 100, w)}</span>}
            <button
              type="button"
              className="rt-del"
              aria-label={`Position ${n} entfernen`}
              disabled={rows.length === 1}
              onClick={() => onChange(rows.filter((x) => x.key !== r.key))}
            >
              ×
            </button>
            {(r.tip || speicherbar || imLager) && (
              <p className="pos-unter">
                {r.tip && <span className="rt-tip-line">{r.tip}</span>}
                {imLager && (
                  <LagerMarke row={r} artikel={leistungen.list} onLoesen={() => setRow(r.key, { artikelId: undefined })} />
                )}
                {speicherbar && (
                  <button type="button" className="linkbtn pos-save" disabled={saving === r.key} onClick={() => void save(r)}>
                    Als Leistung speichern
                  </button>
                )}
              </p>
            )}
          </div>
        );
      })}
      <datalist id="rt-units">
        {UNITS.map((u) => (
          <option key={u} value={u} />
        ))}
      </datalist>
      <div className="pos-add">
        <button type="button" className="linkbtn rt-add" onClick={() => onChange([...rows, { ...emptyRow(), rate: ku ? 19 : satz }])}>
          + Position hinzufügen
        </button>
        {preise && !rabatt && (
          <button type="button" className="linkbtn pos-rabatt" onClick={() => setRabattAn(true)}>
            Rabatt
          </button>
        )}
      </div>
    </div>
  );
}

// Summen unter den Positionen: netto, USt je Satz, Gesamtbetrag; bei der Schlussrechnung abzüglich der Abschläge.
// Props: rows, ku, abzug (Cent, 0: keiner), titel (Text der letzten Zeile, z. B. „Angebotssumme“), w und kurs (Währung
// des Belegs und Kurs; mit USt dazu die Umsatzsteuer in Euro, je Steuerart umgerechnet wie auf dem Server)
export function Summen({
  rows,
  ku,
  abzug = 0,
  titel = 'Rechnungsbetrag',
  w = 'EUR',
  kurs = null,
}: {
  rows: PosRow[];
  ku: boolean;
  abzug?: number;
  titel?: string;
  w?: Waehrung;
  kurs?: string | null;
}) {
  const s = summen(rows, ku);
  const ustEur = w !== 'EUR' && kurs && s.vat !== 0 ? s.groups.reduce((x, [key, n]) => x + inEur(vatOf(key, n), kurs), 0) : null;
  return (
    <div className="rt-sums">
      {!ku && (
        <>
          <div>
            <span>Summe netto</span>
            <span className="num">{eurC(s.net / 100, w)}</span>
          </div>
          {s.groups.map(([key, n]) => (
            <div key={key}>
              <span>{RATE_SUM[key] ?? `zzgl. ${key} % USt`}</span>
              <span className="num">{eurC(vatOf(key, n) / 100, w)}</span>
            </div>
          ))}
        </>
      )}
      {abzug > 0 && (
        <>
          <div>
            <span>Gesamtbetrag</span>
            <span className="num">{eurC(s.gross / 100, w)}</span>
          </div>
          <div>
            <span>abzgl. Abschläge</span>
            <span className="num">{eurC(-abzug / 100, w)}</span>
          </div>
        </>
      )}
      <div className="rt-total">
        <span>{abzug ? 'Zu zahlen' : titel}</span>
        <span className="num">{eurC((s.gross - abzug) / 100, w)}</span>
      </div>
      {ustEur !== null && (
        <div className="rt-ust-eur">
          <span>Umsatzsteuer in Euro</span>
          <span className="num">{eurC(ustEur / 100)}</span>
        </div>
      )}
      {ku && <small className="muted">Ohne Umsatzsteuer (Kleinunternehmer)</small>}
    </div>
  );
}

// Hinweis bei 0 % USt im Formular (Rechnung, Angebot, wiederkehrende Rechnung): der eigene Text des Mandanten, z. B. zu
// § 13b. Sichtbar und mitgeschickt nur, solange eine Position 0 % hat (nicht beim Kleinunternehmer). Erscheint eine
// solche Position und ist das Feld leer, steht der zuletzt benutzte Hinweis der Firma darin (Overview.hinweis0).
// Fehlt der Hinweis, meldet der Server 400 mit code 'steuer_hinweis': die Meldung steht dann am Feld.
// drittland (Kunde außerhalb der EU, formular.ts drittlandArt): bei einer Firma steht statt des gemerkten Hinweises der
// Satz zur nicht steuerbaren Leistung darin, bei einer Privatperson nichts; unter dem Feld Chips mit den Vorschlägen.
export function useSteuerHinweis(rows: PosRow[], ku: boolean, hinweis0: string | null, drittland: 'firma' | 'privat' | null = null) {
  const [text, setText] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  // der Text, auch vor dem nächsten Rendern (alles Setzen geht über setze)
  const aktuell = useRef('');
  // zuletzt von selbst eingesetzter Text (gemerkter Hinweis, Empfehlung): Steht er unverändert da, darf eine neue
  // Empfehlung ihn ersetzen. Was der Mandant schreibt, bleibt.
  const vorschlag = useRef('');
  const sichtbar = !ku && rows.some((r) => r.rate === 0);

  function setze(v: string) {
    aktuell.current = v;
    setText(v);
  }
  // von selbst einsetzen: in ein leeres Feld, mit statt auch statt des vorigen Vorschlags; die Meldung am Feld ist dann
  // erledigt
  function einsetzen(neu: string, statt: boolean) {
    const t = aktuell.current;
    if (t.trim() && !(statt && t === vorschlag.current)) return;
    vorschlag.current = neu;
    setze(neu);
    if (neu) setFehler(null);
  }

  const start = drittland === 'firma' ? HINWEIS_DRITTLAND : drittland === 'privat' ? null : hinweis0;
  useEffect(() => {
    if (sichtbar && start) einsetzen(start, false);
    // nur, wenn eine Position mit 0 % dazukommt
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sichtbar]);
  // Kunde außerhalb der EU gewählt (oder nicht mehr): ein von selbst eingesetzter Hinweis wird ersetzt
  const ersterLauf = useRef(true);
  useEffect(() => {
    if (ersterLauf.current) {
      ersterLauf.current = false;
      return;
    }
    if (sichtbar) einsetzen(start ?? '', true);
    // nur beim Wechsel der Art des Kunden
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drittland]);
  // Meldung vom Server: zum Feld (nach dem Schließen der Rückfrage, die sonst den Fokus zurückgibt)
  useEffect(() => {
    if (fehler) ref.current?.focus();
  }, [fehler]);

  return {
    text,
    fehler,
    sichtbar,
    drittland,
    ref,
    set(v: string) {
      setze(v);
      setFehler(null);
    },
    // aus einem gespeicherten Stand (Kopie, Entwurf, Vorlage, Angebot): sein eigener Hinweis gilt; für alte Positionen mit
    // „13b“ ohne Hinweis der Ersatz aus hinweisAus, aber nur in ein leeres Feld
    laden(items: readonly { rate?: unknown }[], hinweis?: string | null) {
      const neu = hinweisAus(items, hinweis, hinweis0, drittland);
      if (!neu || (aktuell.current.trim() && !hinweis?.trim())) return;
      vorschlag.current = '';
      setze(neu);
      setFehler(null);
    },
    // Empfehlung aus einer alten Rechnung: ihr Hinweis (wie bei laden) in ein leeres Feld. ersetzen: Ihre Zeilen ersetzen
    // die vorigen, dann auch statt eines vorigen Vorschlags, und ohne Hinweis fällt der vorige Vorschlag weg; sonst kommen
    // sie dazu (Übernehmen).
    vorschlagen(items: readonly { rate?: unknown }[], hinweis: string | null | undefined, ersetzen: boolean) {
      const neu = hinweisAus(items, hinweis, hinweis0, drittland);
      if (neu || ersetzen) einsetzen(neu, ersetzen);
    },
    // Text für den Body: null ohne Position mit 0 %
    body: sichtbar ? text.trim() || null : null,
    // Fehler beim Schreiben, Speichern oder in der Vorschau: true, wenn er zum Feld gehört (steht dann dort)
    amFeld(err: unknown) {
      if (!(err instanceof ApiError && err.code === 'steuer_hinweis' && sichtbar)) return false;
      setFehler(errorText(err));
      return true;
    },
  };
}
export type SteuerHinweis = ReturnType<typeof useSteuerHinweis>;

// Feld „Hinweis bei 0 % USt“ direkt unter den Positionen (nur solange eine Position 0 % hat).
// Props: h (useSteuerHinweis), auf: wo er steht („der Rechnung“, „dem Angebot“, …)
export function SteuerHinweisFeld({
  h,
  auf,
}: {
  h: SteuerHinweis;
  auf: 'der Rechnung' | 'dem Angebot' | 'der Auftragsbestätigung' | 'dem Lieferschein';
}) {
  if (!h.sichtbar) return null;
  return (
    <div className="rt-grid rf-null">
      <label className="field rt-span4">
        <span>Hinweis bei 0 % USt</span>
        <textarea
          ref={h.ref}
          value={h.text}
          rows={2}
          maxLength={500}
          placeholder="z. B. Steuerschuldnerschaft des Leistungsempfängers"
          aria-invalid={h.fehler ? true : undefined}
          onChange={(e) => h.set(e.target.value)}
        />
        {h.fehler && <small className="err">{h.fehler}</small>}
        <small>Steht auf {auf}, wenn eine Position 0 % hat.</small>
      </label>
      {h.drittland && (
        <div className="rt-span4 rt-chips rf-null-chips" role="group" aria-label="Vorschläge für den Hinweis">
          {DRITTLAND_HINWEISE[h.drittland].map(([label, satz]) => (
            <button key={label} type="button" className={`rt-chip${h.text.trim() === satz ? ' on' : ''}`} onClick={() => h.set(satz)}>
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
